import { APPETITE_REMINDER, DISCOVERY_FORM_PATH, GATEKEEPER_CARRIER_OPTIONS, LIBERTY_APPOINTMENT_NOTE, LOBS, TIERS, type TierKey } from '@commpass/shared';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AgencyPicker, type SelectedAgency } from '../components/AgencyPicker';
import { ArrowIcon, BackIcon, CheckIcon, DocIcon, DownloadIcon, EyeIcon, InfoIcon, PlusIcon } from '../components/Icons';
import { Modal } from '../components/Modal';
import { Turnstile, turnstileConfigured } from '../components/Turnstile';
import { useToast } from '../components/Toast';
import { ApiError, api } from '../lib/api';
import { currencyInput, phoneInput } from '../lib/format';
import { addRecent } from '../lib/recent';

const STEP_NAMES = ['Tier', 'Agency', 'Insured', 'Documents', 'Review'];
const DISCOVERY_URL = DISCOVERY_FORM_PATH;

// The Gatekeeper carrier sub-screen lists the standard three (BITCO/EMC/FCCI).
// Liberty is chosen on the tier step as its own appointment track, not here.
const GK_STANDARD = GATEKEEPER_CARRIER_OPTIONS.filter((o) => !o.needsAppointmentAck);

// Carrier logo files in /public/carriers.
const CARRIER_LOGO: Record<string, string> = {
  bitco: '/carriers/bitco.png',
  emc: '/carriers/emc.png',
  fcci: '/carriers/fcci.png',
  liberty: '/carriers/liberty-mutual.png',
};

interface DocRow {
  id: string;
  kind: string;
  label: string;
  required: boolean;
  requested?: boolean;
  attached: boolean;
  fileName?: string;
  storagePath?: string;
  skipReason?: string;
}
type Draft = Record<string, unknown> & { tier?: string; lines?: string[]; documents?: DocRow[] };

const docId = () => 'd_' + Math.random().toString(36).slice(2, 9);

function defaultDocs(tier: string): DocRow[] {
  if (tier === 'gatekeeper' || tier === 'liberty')
    return [
      { id: docId(), kind: 'acord', label: 'ACORD Applications (125 / 126 / 140 as applicable)', required: true, attached: false },
      { id: docId(), kind: 'supplemental', label: 'Supplemental Application(s) for the class', required: true, attached: false },
      { id: docId(), kind: 'lossruns', label: '5-Year Currently Valued Loss Runs', required: true, attached: false },
      { id: docId(), kind: 'decpage', label: 'Current Declaration / Dec Pages', required: true, attached: false },
    ];
  if (tier === 'assist' || tier === 'launch')
    return [
      { id: docId(), kind: 'discovery', label: 'Completed Discovery — Commercial Lines New Business', required: true, attached: false },
      { id: docId(), kind: 'lossruns', label: '5-Year Currently Valued Loss Runs', required: true, attached: false },
    ];
  return [];
}

function freshDraft(): Draft {
  return {
    tier: '', agencyName: '', agencyMemberId: '', agencyAccountId: '', linkedMember: null,
    producer: '', submitterPhone: '', submitterEmail: '',
    businessName: '', inAms: '', amsNumber: '', primaryContact: '', phone: '', email: '',
    physicalAddress: '', mailingAddress: '', website: '', fein: '', yearsInBusiness: '', sicNaics: '',
    lines: [], incumbentCarrier: '', policyExpiration: '', quoteNeedBy: '', targetPricing: '',
    accountType: '', quoteTiming: '', isRenewal: '', isMidTerm: '', midTermReason: '',
    descriptionOfOps: '', executiveSummary: '', grossAnnualRevenue: '', annualPayroll: '',
    knownLosses: '', referredBy: '', additionalDetails: '', documents: [],
  };
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

const EMAIL_RE = /^[^@]+@[^@]+\.[^@]+$/;

// Reject the placeholder junk agents type just to clear a required field
// (e.g. "Unknown", "0", "n/a"). A real business name / address has letters and
// isn't one of these throwaway tokens.
const PLACEHOLDER_WORDS = new Set([
  'unknown', 'n/a', 'na', 'none', 'tbd', 'tba', 'test', 'testing', 'xxx', 'x',
  'asdf', 'null', 'nil', '?', '-', '--', '.', 'same',
]);
const hasLetters = (v: string) => /[a-z]/i.test(v);
const isPlaceholder = (v: string) => {
  const t = v.trim().toLowerCase();
  return !t || PLACEHOLDER_WORDS.has(t) || !hasLetters(t);
};

// We ask for ~7 business days to quote. A need-by date sooner than that is a "rush":
// allowed, but we warn the agent we can't promise it.
function rushThresholdMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  let added = 0;
  while (added < 7) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) added++;
  }
  return d.getTime();
}
function isRushDate(v: string): boolean {
  if (!v) return false;
  const t = Date.parse(`${v}T00:00:00`);
  return !Number.isNaN(t) && t < rushThresholdMs();
}

export function Wizard() {
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const resume = (location.state as { resume?: { data: Draft } } | null)?.resume;

  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<Draft>(resume?.data ?? freshDraft());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [docGate, setDocGate] = useState(false);
  // Gatekeeper-only sub-screen of step 1: pick which carrier(s) to submit to.
  const [gkCarrierStep, setGkCarrierStep] = useState(false);
  const [showLibertyConfirm, setShowLibertyConfirm] = useState(false);
  // Which required-carrier path the agent chose on the tier step: the Gatekeeper
  // carriers (BITCO/EMC/FCCI) or the separate Liberty Mutual appointment track.
  const [gkMode, setGkMode] = useState<'' | 'carriers' | 'liberty'>(() => {
    const rd = resume?.data as Draft | undefined;
    if (!rd || rd.tier !== 'gatekeeper') return '';
    const gc = Array.isArray(rd.gatekeeperCarriers) ? (rd.gatekeeperCarriers as { key: string }[]) : [];
    return gc.length === 1 && gc[0]?.key === 'liberty' ? 'liberty' : 'carriers';
  });
  const [showDocConfirm, setShowDocConfirm] = useState(false);
  const [showNoDocs, setShowNoDocs] = useState(false);
  const [showRushWarning, setShowRushWarning] = useState(false);
  const [pin, setPin] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState<{ id: string; pin: string; draft?: boolean } | null>(null);
  const [showSavePin, setShowSavePin] = useState(false);
  const [savePin, setSavePin] = useState('');

  const set = (k: string, v: unknown) =>
    setDraft((d) => {
      const next = { ...d, [k]: v };
      // Q2 (quoteTiming) also drives the legacy renewal / mid-term fields we keep
      // populated for the Salesforce/Power Automate mapping.
      if (k === 'quoteTiming') {
        next.isMidTerm = v === 'midterm' ? 'yes' : 'no';
        next.isRenewal = v === 'renewal' ? 'yes' : 'no';
      }
      return next;
    });
  const str = (k: string) => (draft[k] as string) ?? '';
  const docs = draft.documents ?? [];
  const setDocs = (next: DocRow[] | ((cur: DocRow[]) => DocRow[])) =>
    setDraft((d) => ({
      ...d,
      documents: typeof next === 'function' ? next(d.documents ?? []) : next,
    }));

  const tierKey = draft.tier as TierKey | '';
  const locked = tierKey === 'assist' || tierKey === 'launch';
  const gk = tierKey === 'gatekeeper';
  const liberty = tierKey === 'liberty';
  const gkLike = gk || liberty; // complete-package + exec-summary requirements apply to both

  // Standard commission tiers (Complete / Assist / Launch). Clears any required-carrier state.
  function pickTier(k: TierKey) {
    setGkCarrierStep(false);
    setGkMode('');
    setDraft((d) => ({ ...d, tier: k, documents: defaultDocs(k), gatekeeperCarriers: [] }));
  }
  function toggleLob(id: string) {
    const lines = draft.lines ?? [];
    set('lines', lines.includes(id) ? lines.filter((l) => l !== id) : [...lines, id]);
  }

  // ---- Required-carrier paths (agent side) ----
  type GkPick = { key: string; status: string; at?: number };
  const gkSel: GkPick[] = Array.isArray(draft.gatekeeperCarriers) ? (draft.gatekeeperCarriers as GkPick[]) : [];
  const gkHas = (k: string) => gkSel.some((c) => c.key === k);

  function gkSet(next: GkPick[]) {
    setDraft((d) => ({ ...d, gatekeeperCarriers: next }));
    if (next.length) setErrors((e) => ({ ...e, gkCarriers: '' }));
  }
  // Gatekeeper card: BITCO/EMC/FCCI. Routes to the carrier sub-screen on Continue.
  function chooseGatekeeper() {
    setGkCarrierStep(false);
    setGkMode('carriers');
    setDraft((d) => ({ ...d, tier: 'gatekeeper', documents: defaultDocs('gatekeeper'), gatekeeperCarriers: [] }));
  }
  // Liberty card: a direct-appointment track (still handled by AAS under the hood).
  // Requires the appointment acknowledgment before it's selected.
  function chooseLiberty() {
    setShowLibertyConfirm(true);
  }
  function confirmLiberty() {
    setGkCarrierStep(false);
    setGkMode('liberty');
    setDraft((d) => ({
      ...d,
      tier: 'liberty',
      documents: defaultDocs('liberty'),
      gatekeeperCarriers: [{ key: 'liberty', status: 'pending', at: Date.now() }],
    }));
    setShowLibertyConfirm(false);
  }
  // Toggle a Gatekeeper carrier in the sub-screen (BITCO/EMC/FCCI only).
  function gkToggle(k: string) {
    gkSet(gkHas(k) ? gkSel.filter((c) => c.key !== k) : [...gkSel, { key: k, status: 'pending', at: Date.now() }]);
  }
  // Per-carrier appetite acknowledgment — the agent must confirm the risk fits each
  // selected carrier's appetite (after reviewing its guide) before continuing.
  const appetiteAck = (draft.appetiteAck as Record<string, boolean>) ?? {};
  function setAppetiteAck(key: string, val: boolean) {
    setDraft((d) => ({ ...d, appetiteAck: { ...((d.appetiteAck as Record<string, boolean>) ?? {}), [key]: val } }));
    if (val) setErrors((e) => ({ ...e, gkCarriers: '' }));
  }

  // Step 1 (tier) "Continue": the Gatekeeper carrier path routes to the sub-screen first;
  // everything else (Liberty appointment or a standard tier) goes straight to step 2.
  function tierContinue() {
    if (!draft.tier) {
      setErrors({ tier: 'Select a service tier to continue.' });
      return;
    }
    setErrors({});
    if (draft.tier === 'gatekeeper' && gkMode === 'carriers' && !gkCarrierStep) {
      setGkCarrierStep(true);
      window.scrollTo({ top: 0 });
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0 });
  }
  // Gatekeeper carrier sub-screen "Continue": require at least one carrier.
  function gkContinue() {
    if (gkSel.length === 0) {
      setErrors({ gkCarriers: 'Select at least one Gatekeeper carrier to continue.' });
      return;
    }
    const ack = (draft.appetiteAck as Record<string, boolean>) ?? {};
    if (gkSel.some((c) => !ack[c.key])) {
      setErrors({ gkCarriers: "Review each carrier's appetite guide and check the box to confirm the risk fits." });
      return;
    }
    setErrors({});
    setStep(2);
    window.scrollTo({ top: 0 });
  }

  // Returns true only if the file actually landed in storage. Callers use the result
  // to decide whether a row should survive (a rejected upload must not leave a phantom
  // "Not attached" row that looks like a document we were given).
  async function uploadToEntry(entryId: string, file: File): Promise<boolean> {
    try {
      const data = await fileToBase64(file);
      const res = await api.uploadDoc({
        submissionCode: (draft.id as string) || undefined,
        fileName: file.name,
        mime: file.type || 'application/octet-stream',
        data,
        turnstileToken: turnstileToken || undefined,
      });
      setDocs((cur) =>
        cur.map((d) => (d.id === entryId ? { ...d, attached: true, fileName: res.fileName, storagePath: res.storagePath } : d)),
      );
      toast(`Uploaded ${file.name}`);
      return true;
    } catch (e) {
      // .zip is the common miss — agents bundle a folder of docs and it's rejected.
      // Say what to do instead rather than a bare "Unsupported file type".
      const isZip = /\.zip$/i.test(file.name) || /zip/i.test(file.type);
      toast(
        isZip
          ? "We can't open .zip files. Please drag and drop the individual files into the portal, or email them to CommPass@agentsalliance.biz and we'll add them for you."
          : e instanceof ApiError
            ? e.message
            : 'Upload failed',
      );
      return false;
    }
  }

  async function addExtraDoc(file: File) {
    const entry: DocRow = { id: docId(), kind: 'other', label: file.name, required: false, attached: false };
    setDocs((cur) => [...cur, entry]);
    const ok = await uploadToEntry(entry.id, file);
    // Drop the row if the upload was rejected, so it doesn't linger as a phantom
    // "Not attached" document the agent thinks they provided.
    if (!ok) setDocs((cur) => cur.filter((d) => d.id !== entry.id));
  }

  function removeDoc(entryId: string) {
    setDocs((cur) => cur.filter((d) => d.id !== entryId));
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (step === 1 && !draft.tier) e.tier = 'Select a service tier to continue.';
    if (step === 2) {
      if (!str('agencyMemberId') && !str('agencyName'))
        e.agencyName = 'Enter your agency name, then pick it from the list — or add it as typed if it is not listed yet.';
      if (!str('producer').trim()) e.producer = "Enter the submitter's name.";
      if (!str('submitterPhone').trim()) e.submitterPhone = "Enter the submitter's phone number.";
      if (!EMAIL_RE.test(str('submitterEmail').trim())) e.submitterEmail = 'Enter a valid email.';
    }
    if (step === 3) {
      if (!str('businessName').trim()) e.businessName = 'Enter the business name.';
      else if (isPlaceholder(str('businessName')))
        e.businessName = 'Enter the real business name — a placeholder like “Unknown” or “0” won’t work.';
      if (!EMAIL_RE.test(str('email').trim())) e.email = 'Enter a valid email.';
      if (!str('phone').trim()) e.phone = 'Enter a phone number.';
      if (!str('physicalAddress').trim()) e.physicalAddress = 'Enter the physical address.';
      else if (!hasLetters(str('physicalAddress')))
        e.physicalAddress = 'Enter a real physical address — street, city, and state.';
      if (!str('yearsInBusiness').trim()) e.yearsInBusiness = 'Enter years in business.';
      if (!str('inAms')) e.inAms = 'Let us know if this account is already in AMS360.';
      else if (str('inAms') === 'yes' && !str('amsNumber').trim()) e.amsNumber = 'Enter the AMS360 # (see the tooltip for where to find it).';
      if (!(draft.lines ?? []).length) e.lines = 'Select at least one line of business.';
      if (!str('quoteNeedBy')) e.quoteNeedBy = 'Choose a quote need-by date.';
      if (!str('descriptionOfOps').trim()) e.descriptionOfOps = 'Describe the operations.';
      if (!str('accountType')) e.accountType = 'Let us know if this is new business or an account you already own.';
      else if (!str('quoteTiming')) e.quoteTiming = "Let us know the timing — at renewal or a mid-term request.";
      else if (str('quoteTiming') === 'midterm' && !str('midTermReason').trim())
        e.midTermReason = 'Carriers need the reason for a mid-term request — please explain.';
      if (gkLike && !str('executiveSummary').trim())
        e.executiveSummary = `An executive summary is required for ${liberty ? 'Liberty Mutual' : 'Gatekeeper'} submissions.`;
    }
    if (step === 4) {
      // Loss runs: a reason is ALWAYS required if they aren't attached — every tier
      // that lists them (Assist/Launch/Gatekeeper/Liberty). This guarantees staff always
      // know *why* loss runs are missing, in one consistent place.
      const lr = docs.find((d) => d.kind === 'lossruns');
      const lrMissing = !!lr && !lr.attached && !(lr.skipReason || '').trim();
      // Assist/Launch additionally require an attachment-or-reason on every required doc.
      const lockedGaps = locked
        ? docs.filter((d) => d.required && !d.attached && !(d.skipReason || '').trim())
        : [];
      if (lrMissing || lockedGaps.length) {
        setDocGate(true);
        e.docs =
          lrMissing && !lockedGaps.length
            ? "Loss runs are required — attach them, or enter the reason they're not included, to continue."
            : 'Upload each required document — or add a short reason under it — to continue.';
      } else {
        setDocGate(false);
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (!validate()) return;
    // Alliance Assist/Launch: if a required doc is backed by a reason instead of the
    // actual file, confirm the possible Tier-1 downgrade before moving to Review.
    if (step === 4 && locked && docs.some((d) => d.required && !d.attached && (d.skipReason || '').trim())) {
      setShowDocConfirm(true);
      return;
    }
    setStep((s) => Math.min(5, s + 1));
    window.scrollTo({ top: 0 });
  }
  function confirmDocsAndReview() {
    setShowDocConfirm(false);
    setStep(5);
    window.scrollTo({ top: 0 });
  }
  function back() {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0 });
  }

  async function persist(status: string, thePin: string): Promise<string | null> {
    try {
      const { id } = await api.createSubmission({
        data: { ...draft, status },
        pin: thePin,
        turnstileToken: turnstileToken || undefined,
      });
      return id;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save');
      return null;
    }
  }

  async function doSaveDraft() {
    if (savePin.length !== 4) {
      toast('Set a 4-digit PIN');
      return;
    }
    setBusy(true);
    const id = await persist('draft', savePin);
    setBusy(false);
    if (id) {
      setShowSavePin(false);
      setSubmitted({ id, pin: savePin, draft: true });
      addRecent({ code: id, businessName: str('businessName'), status: 'draft' });
    }
  }

  async function submit() {
    if (pin.length !== 4) {
      toast('Set a 4-digit PIN to secure your submission');
      return;
    }
    if (turnstileConfigured && !turnstileToken) {
      toast('Please complete the bot check first');
      return;
    }
    // Guard against unknowingly sending an empty submission: if no file is actually
    // attached, make the agent explicitly confirm (or go back to Documents and attach).
    const anyDocAttached = (draft.documents ?? []).some((d) => d.storagePath);
    if (!anyDocAttached) {
      setShowNoDocs(true);
      return;
    }
    await doSubmit();
  }

  async function doSubmit() {
    setShowNoDocs(false);
    setBusy(true);
    const id = await persist('received', pin);
    setBusy(false);
    if (id) {
      setSubmitted({ id, pin });
      addRecent({ code: id, businessName: str('businessName'), status: 'received' });
    }
  }

  const Radio = ({ field, value, label }: { field: string; value: string; label: string }) => (
    <label className={`radio-opt${str(field) === value ? ' sel' : ''}`}>
      <input type="radio" name={field} checked={str(field) === value} onChange={() => set(field, value)} /> {label}
    </label>
  );
  const Err = ({ field }: { field: string }) =>
    errors[field] ? <div className="ferr show">{errors[field]}</div> : null;

  const acctLabel =
    str('accountType') === 'new_agency' ? 'New business for the agency' : str('accountType') === 'owned' ? 'Account I already own' : '';
  const timingLabel =
    str('quoteTiming') === 'renewal' ? 'At/near renewal' : str('quoteTiming') === 'midterm' ? 'Mid-term quote' : '';
  const reqType = [acctLabel, timingLabel].filter(Boolean).join(' · ') || '—';

  return (
    <div className="wrap wizard">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div className="back-link" onClick={() => navigate('/')}>
          <BackIcon width={16} height={16} /> Cancel
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowSavePin(true)}>
          Save &amp; finish later
        </button>
      </div>
      <div className="page-title" style={{ marginBottom: 6 }}>New COMM-PASS submission</div>
      {draft.id ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 13px', margin: '0 0 14px', fontSize: 13, color: '#92400E' }}>
          Your code <b style={{ letterSpacing: 1, fontSize: 15 }}>{draft.id as string}</b> — write it down to return later.
        </div>
      ) : null}

      <div className="steps">
        {STEP_NAMES.map((n, i) => {
          const num = i + 1;
          const cls = num < step ? 'done' : num === step ? 'on' : '';
          return (
            <div key={n} style={{ display: 'contents' }}>
              <div className={`step ${cls}`}>
                <span className="scount">{num < step ? '✓' : num}</span>
                <span>{n}</span>
              </div>
              {i < STEP_NAMES.length - 1 && <div className={`step-sep ${num < step ? 'done' : ''}`} />}
            </div>
          );
        })}
      </div>

      <div className="form-card">
        {/* STEP 1 — TIER */}
        {step === 1 && !gkCarrierStep && (
          <>
            <div className="section-label">Choose your service tier</div>
            <p style={{ fontSize: 13.5, color: 'var(--muted)', marginBottom: 16 }}>
              How much help do you want? Pick a commission-split tier below, or choose <b>Gatekeeper</b> for a carrier we're
              required to submit on your behalf. The form adapts to what you pick.
            </p>
            <div className="tier-grid">
              {(['complete', 'assist', 'launch'] as const).map((k) => {
                const t = TIERS[k];
                const sel = draft.tier === k;
                return (
                  <div key={k} className={`tier-card${sel ? ' sel' : ''}`} onClick={() => pickTier(k)}>
                    <div className="tier-band">{t.band}</div>
                    <div className="tier-name">{t.name}</div>
                    <div className="tier-tag">{t.tag}</div>
                    <div className="tier-split">Commission: <b>{t.member}%</b> you · {t.aas}% AAS</div>
                    <div className="tier-desc">
                      <b>Who it's for:</b> {t.who}
                      <br />
                      <br />
                      <b>What AAS does:</b> {t.does}
                    </div>
                    <div className="tier-pick">{sel ? 'Selected' : 'Choose this tier'}</div>
                  </div>
                );
              })}
            </div>
            <div className="gk-or"><span>or, for a required carrier</span></div>
            <div className="gk-two">
              {/* Gatekeeper card — BITCO / EMC / FCCI (maroon required-carrier theme) */}
              <div className={`gk-card${gkMode === 'carriers' ? ' sel' : ''}`}>
                <div className="gk-card-body">
                  <div className="tier-band" style={{ color: 'var(--maroon)' }}>Required Carrier · Handled by AAS</div>
                  <div className="tier-name">Gatekeeper Submission</div>
                  <p className="gk-desc">
                    For carriers we're required to submit on your behalf. Send a <b>complete package</b> — ACORDs,
                    supplementals, loss runs, dec pages — plus a description of operations and an executive summary. You
                    can submit before it's all in, but <b>AAS won't act until the submission is complete.</b>
                  </p>
                  <div className="gk-carrier-label">Gatekeeper carriers</div>
                  <div className="gk-carrier-logos">
                    {GK_STANDARD.map((o) => (
                      <CarrierLogo key={o.key} name={o.name} logo={CARRIER_LOGO[o.key]} />
                    ))}
                  </div>
                </div>
                <button className={`btn ${gkMode === 'carriers' ? 'btn-maroon-sel' : 'btn-maroon'}`} onClick={chooseGatekeeper}>
                  {gkMode === 'carriers' ? '✓ Selected' : 'Choose Gatekeeper'}
                </button>
              </div>

              {/* Liberty Mutual card — direct-appointment track (Liberty blue theme) */}
              <div className={`gk-card gk-lib${gkMode === 'liberty' ? ' sel' : ''}`}>
                <div className="gk-card-body">
                  <CarrierLogo name="Liberty Mutual" logo={CARRIER_LOGO.liberty} big hideOnFail />
                  <div className="tier-band" style={{ color: '#1B4CA1' }}>Direct Appointment Track</div>
                  <div className="tier-name" style={{ color: '#1B4CA1' }}>Liberty Mutual</div>
                  <p className="gk-desc">
                    <b>Not a Gatekeeper submission.</b> Choose this only if you're seeking a <b>direct appointment</b> with
                    Liberty Mutual. Liberty isn't for one-offs — they want to see a few consistent, in-appetite accounts
                    bound in a relatively short time before they'll consider appointing you. Have a one-off? Send it to
                    {' '}<b>RPS</b> instead.
                  </p>
                </div>
                <button className={`btn ${gkMode === 'liberty' ? 'btn-liberty-sel' : 'btn-liberty'}`} onClick={chooseLiberty}>
                  {gkMode === 'liberty' ? '✓ Selected' : 'Choose Liberty Mutual'}
                </button>
              </div>
            </div>
            <Err field="tier" />
            <div className="wiz-foot">
              <span />
              <button className="btn btn-primary" onClick={tierContinue}>Continue <ArrowIcon width={16} height={16} /></button>
            </div>
          </>
        )}

        {/* STEP 1b — GATEKEEPER CARRIER SELECTION (required for Gatekeeper) */}
        {step === 1 && gkCarrierStep && (
          <>
            <div className="section-label">Which Gatekeeper carrier(s)?</div>
            <p style={{ fontSize: 13.5, color: 'var(--muted)', marginBottom: 16 }}>
              Choose the carrier or carriers you'd like to quote with — select all that apply. Once we have a
              complete submission (completed ACORDs, completed supplemental, loss runs, dec pages, etc.), we can
              submit to the carrier on your behalf.
            </p>
            <div className="lob-grid">
              {GK_STANDARD.map((o) => {
                const sel = gkHas(o.key);
                return (
                  <div key={o.key} className={`lob-opt${sel ? ' sel' : ''}`} onClick={() => gkToggle(o.key)}>
                    <div className="lob-box">{sel ? <CheckIcon width={13} height={13} /> : null}</div>
                    <CarrierLogo name={o.name} logo={CARRIER_LOGO[o.key]} bare />
                  </div>
                );
              })}
            </div>
            {gkSel.length > 0 && (
              <div style={{ marginTop: 18, display: 'grid', gap: 10 }}>
                <div
                  className="sub-meta"
                  style={{ background: 'var(--slate-soft)', borderLeft: '3px solid var(--maroon)', borderRadius: 8, padding: '10px 12px', lineHeight: 1.5 }}
                >
                  {APPETITE_REMINDER}
                </div>
                {gkHas('bitco') && (
                  <div
                    style={{
                      background: '#FEF2F2',
                      border: '1px solid #DC2626',
                      borderRadius: 8,
                      padding: '10px 12px',
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: '#7F1D1D',
                    }}
                  >
                    <strong>BITCO requires 5-year loss runs.</strong> We can accept your submission, but we cannot
                    submit it to BITCO until loss runs are attached — BITCO will not treat it as complete without
                    them, and a competing agency that submits with loss runs would be taken ahead of you. Please
                    attach loss runs in the Documents step.
                  </div>
                )}
                {gkSel.map((c) => {
                  const o = GK_STANDARD.find((x) => x.key === c.key);
                  if (!o) return null;
                  return (
                    <div key={o.key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <b style={{ color: 'var(--navy)' }}>{o.name}</b>
                        {o.appetiteDoc && (
                          <a className="doc-getlink" href={o.appetiteDoc} target="_blank" rel="noreferrer">
                            <DocIcon width={13} height={13} /> Review {o.name}'s appetite guide (PDF)
                          </a>
                        )}
                      </div>
                      {o.appetiteNote && <div className="sub-meta" style={{ marginTop: 5 }}>{o.appetiteNote}</div>}
                      <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer', marginTop: 10, fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={appetiteAck[o.key] === true}
                          onChange={(e) => setAppetiteAck(o.key, e.target.checked)}
                          style={{ marginTop: 3 }}
                        />
                        <span>
                          I've reviewed <b>{o.name}</b>'s appetite and this risk fits.
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
            <Err field="gkCarriers" />
            <div className="wiz-foot">
              <button className="btn btn-ghost" onClick={() => { setGkCarrierStep(false); setErrors({}); window.scrollTo({ top: 0 }); }}>
                <BackIcon width={16} height={16} /> Back
              </button>
              <button className="btn btn-primary" onClick={gkContinue}>Continue <ArrowIcon width={16} height={16} /></button>
            </div>
          </>
        )}

        {/* STEP 2 — AGENCY */}
        {step === 2 && (
          <>
            <div className="section-label">Your agency</div>
            <AgencyPicker
              value={str('agencyName') ? { name: str('agencyName'), memberId: str('agencyMemberId'), accountId: str('agencyAccountId') } : null}
              onSelect={(a: SelectedAgency) => {
                set('agencyName', a.name);
                set('agencyMemberId', a.memberId);
                set('agencyAccountId', a.accountId);
                set('linkedMember', { name: a.name, memberId: a.memberId, accountId: a.accountId, source: a.memberId ? 'directory' : 'typed', confirmed: !!a.memberId });
              }}
              onClear={() => {
                set('agencyName', '');
                set('agencyMemberId', '');
                set('agencyAccountId', '');
                set('linkedMember', null);
              }}
            />
            <Err field="agencyName" />
            <Field label="Your name (producer)" field="producer" value={str('producer')} onChange={set} err={errors.producer} />
            <div className="fgrid">
              <Field label="Your phone" field="submitterPhone" value={str('submitterPhone')} onChange={set} err={errors.submitterPhone} placeholder="254-555-0123" format={phoneInput} />
              <Field label="Your email" field="submitterEmail" value={str('submitterEmail')} onChange={set} err={errors.submitterEmail} placeholder="you@agency.com" />
            </div>
            <div className="wiz-foot">
              <button className="btn btn-ghost" onClick={back}><BackIcon width={16} height={16} /> Back</button>
              <button className="btn btn-primary" onClick={next}>Continue <ArrowIcon width={16} height={16} /></button>
            </div>
          </>
        )}

        {/* STEP 3 — INSURED */}
        {step === 3 && (
          <>
            <div className="section-label">About this request</div>
            <div className="fgroup">
              <label className="flabel">Is this new business for the agency, or an account you already own?</label>
              <div className="radio-row"><Radio field="accountType" value="new_agency" label="New business for the agency" /><Radio field="accountType" value="owned" label="Account I already own" /></div>
              <Err field="accountType" />
            </div>
            {str('accountType') && (
              <div className="fgroup">
                <label className="flabel">Are you shopping at or near the account's renewal, or is this a mid-term quote request?</label>
                <div className="radio-row"><Radio field="quoteTiming" value="renewal" label="At or near renewal" /><Radio field="quoteTiming" value="midterm" label="Mid-term quote request" /></div>
                <Err field="quoteTiming" />
              </div>
            )}
            {str('quoteTiming') === 'midterm' && (
              <>
                <div className="banner warn" style={{ margin: '0 0 12px' }}>
                  <div style={{ fontSize: 12.5, display: 'flex', gap: 8 }}>
                    <InfoIcon width={16} height={16} /> <span>Most carriers won't consider an account for mid-term quoting without a <b>good reason</b>. What is the insured's reasoning?</span>
                  </div>
                </div>
                <Area label="Reason for the mid-term request" field="midTermReason" value={str('midTermReason')} onChange={set} err={errors.midTermReason}
                  placeholder="e.g. Current carrier is non-renewing, a major coverage gap was discovered, recent acquisition, unresolved dispute with the current carrier, etc." />
              </>
            )}

            <div className="section-label">General insured information</div>
            <Field label="Business name" field="businessName" value={str('businessName')} onChange={set} err={errors.businessName} placeholder="Legal business name" />
            <div className="fgroup">
              <label className="flabel">Have you already entered this account into AMS360?</label>
              <div className="radio-row"><Radio field="inAms" value="no" label="No" /><Radio field="inAms" value="yes" label="Yes" /></div>
              <Err field="inAms" />
            </div>
            {str('inAms') === 'yes' && (
              <Field label="AMS360 #" field="amsNumber" value={str('amsNumber')} onChange={set} err={errors.amsNumber} placeholder="e.g. 00017601" />
            )}
            <Field label="Primary contact (owner / insurance contact)" field="primaryContact" value={str('primaryContact')} onChange={set} placeholder="Contact name" />
            <div className="fgrid">
              <Field label="Phone number" field="phone" value={str('phone')} onChange={set} err={errors.phone} placeholder="254-555-0123" format={phoneInput} />
              <Field label="Email address" field="email" value={str('email')} onChange={set} err={errors.email} placeholder="contact@business.com" />
            </div>
            <div className="fgrid">
              <Field label="Physical address" field="physicalAddress" value={str('physicalAddress')} onChange={set} err={errors.physicalAddress} placeholder="Street, City, ST" />
              <Field label="Mailing address" field="mailingAddress" value={str('mailingAddress')} onChange={set} opt placeholder="If different" />
            </div>
            <Field label="Company website" field="website" value={str('website')} onChange={set} opt placeholder="https://www.example.com — our team can pull extra detail from it" />
            <div className="fgrid-3">
              <Field label="FEIN" field="fein" value={str('fein')} onChange={set} opt placeholder="XX-XXXXXXX" />
              <Field label="Years in business" field="yearsInBusiness" value={str('yearsInBusiness')} onChange={set} err={errors.yearsInBusiness} placeholder="e.g. 6" />
              <Field label="SIC or NAICS" field="sicNaics" value={str('sicNaics')} onChange={set} opt placeholder="e.g. 238160" />
            </div>

            <div className="section-label">Lines of business quoting</div>
            <div className="lob-grid">
              {LOBS.map((l) => {
                const sel = (draft.lines ?? []).includes(l.id);
                return (
                  <div key={l.id} className={`lob-opt${sel ? ' sel' : ''}`} onClick={() => toggleLob(l.id)}>
                    <div className="lob-box">{sel ? <CheckIcon width={13} height={13} /> : null}</div>
                    {l.name}
                  </div>
                );
              })}
            </div>
            <Err field="lines" />
            <div className="fgrid" style={{ marginTop: 16 }}>
              <Field label="Incumbent carrier" field="incumbentCarrier" value={str('incumbentCarrier')} onChange={set} opt placeholder="Current carrier" />
              <div className="fgroup">
                <label className="flabel">Policy expiration date <span className="opt">(optional)</span></label>
                <input type="date" className="finput" value={str('policyExpiration')} onChange={(e) => set('policyExpiration', e.target.value)} />
              </div>
            </div>
            <div className="fgrid">
              <div className="fgroup">
                <label className="flabel">Quote need-by date</label>
                <input
                  type="date"
                  className={`finput${errors.quoteNeedBy ? ' err' : ''}`}
                  value={str('quoteNeedBy')}
                  onChange={(e) => {
                    const v = e.target.value;
                    set('quoteNeedBy', v);
                    if (isRushDate(v)) setShowRushWarning(true);
                  }}
                />
                <div className="sub-meta" style={{ marginTop: 4 }}>
                  Quoting usually takes about <b>7 business days</b>. Sooner isn't guaranteed.
                </div>
                <Err field="quoteNeedBy" />
              </div>
              <Field label="Target pricing" field="targetPricing" value={str('targetPricing')} onChange={set} opt placeholder="$ target premium" format={currencyInput} />
            </div>

            <div className="section-label">Detailed insured information</div>
            <Area label="Description of operations" field="descriptionOfOps" value={str('descriptionOfOps')} onChange={set} err={errors.descriptionOfOps} placeholder="What does the business do?" />
            {gkLike && (
              <Area label="Executive summary" field="executiveSummary" value={str('executiveSummary')} onChange={set} err={errors.executiveSummary}
                placeholder="A concise underwriting narrative: the risk, why it's a good account, notable exposures, target coverage, and anything a carrier should know up front." />
            )}
            <div className="fgrid">
              <Field label="Gross annual revenue" field="grossAnnualRevenue" value={str('grossAnnualRevenue')} onChange={set} opt placeholder="$" format={currencyInput} />
              <Field label="Annual payroll" field="annualPayroll" value={str('annualPayroll')} onChange={set} opt placeholder="$" format={currencyInput} />
            </div>
            <Area label="Any known losses (with detail)" field="knownLosses" value={str('knownLosses')} onChange={set} opt placeholder="Summarize claims in the last 5 years." />

            <div className="section-label">Notes</div>
            <Area label="Notes / anything else we should know" field="additionalDetails" value={str('additionalDetails')} onChange={set} opt placeholder="Optional — any statement, context, or special request you want to pass along to the AAS team." />

            <div className="wiz-foot">
              <button className="btn btn-ghost" onClick={back}><BackIcon width={16} height={16} /> Back</button>
              <button className="btn btn-primary" onClick={next}>Continue <ArrowIcon width={16} height={16} /></button>
            </div>
          </>
        )}

        {/* STEP 4 — DOCUMENTS */}
        {step === 4 && (
          <>
            <div className="section-label">Documents</div>
            <p style={{ fontSize: 13.5, color: 'var(--muted)', marginBottom: 10 }}>
              {gkLike
                ? (<>{liberty ? 'Liberty Mutual' : 'Gatekeeper'} submissions require a <b>complete package</b>. The <span className="req-star">*</span> items below are what the carrier needs. You can still submit without most of them — but <b>AAS won't act until the submission is complete.</b> Attach what you have; upload the rest anytime. If you don't have <b>loss runs</b> yet, a short reason is required to continue.</>)
                : locked
                ? (<>For {TIERS[tierKey as TierKey].name}, the <span className="req-star">*</span> documents are required. Upload each one — or, if you don't have it yet, add a short reason. You can't continue with a required document that's both missing and unexplained.</>)
                : (<>For a full-service referral, attachments are optional — our team gathers everything else. Add anything you already have.</>)}
            </p>
            {(locked || gkLike) && (
              <p className="supp-note"><InfoIcon width={14} height={14} /> We may request a <b>Supplemental Application</b> be completed at a later date, if one isn't already attached.</p>
            )}

            {docs.length === 0 && <p style={{ fontSize: 13, color: 'var(--muted)' }}>No documents added yet.</p>}
            {docs.map((d) => {
              // Loss runs always need a reason when unattached (any tier); Assist/Launch
              // require it on every required doc.
              const needReason = d.required && !d.attached && (locked || d.kind === 'lossruns');
              return (
                <div className="up-row" key={d.id}>
                  <div className={`up-icon${d.attached ? ' on' : ''}`}>{d.attached ? <CheckIcon width={16} height={16} /> : <DocIcon width={16} height={16} />}</div>
                  <div className="up-body">
                    <div className="up-name">
                      {d.label} {d.required ? <span className="req-star">*</span> : null}
                    </div>
                    <div className={`up-file${d.attached ? '' : ' none'}`}>{d.attached ? d.fileName : d.required ? 'Recommended — not attached' : 'Not attached'}</div>
                    {d.kind === 'lossruns' && gkHas('bitco') && !d.attached && (
                      <div
                        style={{
                          marginTop: 6,
                          background: '#FEF2F2',
                          border: '1px solid #DC2626',
                          borderRadius: 6,
                          padding: '6px 9px',
                          fontSize: 12.5,
                          color: '#7F1D1D',
                          fontWeight: 600,
                        }}
                      >
                        ⚠ Required for BITCO — we cannot submit to BITCO without loss runs. A reason won't substitute;
                        BITCO needs the actual 5-year loss runs before we can send.
                      </div>
                    )}
                    {d.kind === 'discovery' && (
                      <a className="doc-getlink" href={DISCOVERY_URL} download="Discovery - Commercial Lines New Business.xlsx"><DownloadIcon width={14} height={14} /> Get the blank Discovery form</a>
                    )}
                    {needReason && (
                      <div className={`doc-reason ${(d.skipReason || '').trim() ? 'ok' : 'need'}`}>
                        <label className="doc-reason-label">Don't have this yet? A reason is required to continue without it:</label>
                        <input className="finput sm" value={d.skipReason || ''} placeholder="e.g. Brand-new venture — no prior loss runs exist"
                          onChange={(e) => setDocs(docs.map((x) => (x.id === d.id ? { ...x, skipReason: e.target.value } : x)))} />
                      </div>
                    )}
                  </div>
                  <div className="up-actions">
                    {d.attached && d.storagePath && <EyeIcon width={15} height={15} style={{ color: 'var(--muted)' }} />}
                    <label className={`up-btn${d.attached ? ' has' : ''}`} style={{ cursor: 'pointer' }}>
                      {d.attached ? 'Replace' : 'Upload'}
                      <input type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadToEntry(d.id, f); e.target.value = ''; }} />
                    </label>
                    {d.kind === 'other' && (
                      <button type="button" className="up-remove" title="Remove this document" aria-label="Remove document" onClick={() => removeDoc(d.id)}>×</button>
                    )}
                  </div>
                </div>
              );
            })}

            <label className="btn btn-ghost btn-sm" style={{ marginTop: 14, cursor: 'pointer' }}>
              <PlusIcon width={15} height={15} /> Add a document
              <input type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void addExtraDoc(f); e.target.value = ''; }} />
            </label>
            <Err field="docs" />

            {docGate && locked && (
              <div className="downgrade-box">
                <div className="downgrade-head"><InfoIcon width={16} height={16} /> Don't have a required document yet?</div>
                <p className="downgrade-p">
                  <b>No problem — you keep your {TIERS[tierKey as TierKey].member}% commission.</b> Just add a short reason in the box
                  under each missing document above (for example, <i>"New venture — no prior loss runs exist"</i>), then click
                  {' '}<b>Review</b> to continue under {TIERS[tierKey as TierKey].name}. You don't need to change anything else.
                </p>
                <details style={{ marginTop: 12 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--slate)', fontWeight: 600 }}>
                    Or — would you rather AAS handle the entire account for you?
                  </summary>
                  <div className="downgrade-tier1" style={{ marginTop: 10 }}>
                    <p className="downgrade-p" style={{ margin: '0 0 10px' }}>
                      Only choose this if you'd like AAS to quote, bind, service and renew the whole account for you. It
                      {' '}<b>changes your commission split to {TIERS.complete.member}% you / {TIERS.complete.aas}% AAS</b> ({TIERS.complete.name}).
                    </p>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setDocGate(false); setErrors({}); setDraft((d) => ({ ...d, tier: 'complete', documents: [] })); setStep(5); window.scrollTo({ top: 0 }); }}>
                      Switch to {TIERS.complete.name} ({TIERS.complete.member}% you / {TIERS.complete.aas}% AAS)
                    </button>
                  </div>
                </details>
              </div>
            )}

            <div className="wiz-foot">
              <button className="btn btn-ghost" onClick={back}><BackIcon width={16} height={16} /> Back</button>
              <button className="btn btn-primary" onClick={next}>Review <ArrowIcon width={16} height={16} /></button>
            </div>
          </>
        )}

        {/* STEP 5 — REVIEW */}
        {step === 5 && (
          <>
            <div className="review-block">
              <h4>{gkLike ? 'Submission type' : 'Service tier'}</h4>
              <dl className="kv">
                <dt>{gkLike ? 'Type' : 'Tier'}</dt>
                <dd>{tierKey ? `${TIERS[tierKey].name} — ${TIERS[tierKey].tag}` : '—'}</dd>
                <dt>{gkLike ? 'Handling' : 'Commission'}</dt>
                <dd>
                  {liberty
                    ? 'Direct appointment submission handled by AAS'
                    : gk
                    ? 'Required carrier submission handled by AAS'
                    : tierKey
                    ? `${TIERS[tierKey].member}% you · ${TIERS[tierKey].aas}% AAS`
                    : '—'}
                </dd>
                {gk && (
                  <>
                    <dt>Carriers requested</dt>
                    <dd>
                      {gkSel.map((c) => GATEKEEPER_CARRIER_OPTIONS.find((o) => o.key === c.key)?.name ?? c.key).join(', ') || '—'}
                    </dd>
                  </>
                )}
              </dl>
            </div>
            <div className="review-block">
              <h4>Agency</h4>
              <dl className="kv">
                <dt>Agency name</dt><dd>{str('agencyName') || '—'}</dd>
                <dt>Submitter</dt><dd>{str('producer') || '—'}</dd>
                <dt>Submitter email</dt><dd>{str('submitterEmail') || '—'}</dd>
              </dl>
            </div>
            <div className="review-block">
              <h4>Insured</h4>
              <dl className="kv">
                <dt>Business</dt><dd>{str('businessName') || '—'}</dd>
                <dt>Request type</dt><dd>{reqType}{str('quoteTiming') === 'midterm' && str('midTermReason').trim() ? ` — ${str('midTermReason')}` : ''}</dd>
                <dt>Lines quoting</dt><dd>{(draft.lines ?? []).map((l) => LOBS.find((x) => x.id === l)?.name ?? l).join(', ') || '—'}</dd>
                <dt>Incumbent carrier</dt><dd>{str('incumbentCarrier') || '—'}</dd>
                <dt>Quote need-by</dt><dd>{str('quoteNeedBy') || '—'}</dd>
              </dl>
            </div>
            <div className="review-block">
              <h4>Documents</h4>
              {gkHas('bitco') && !docs.some((d) => d.kind === 'lossruns' && d.attached) && (
                <div
                  style={{
                    background: '#FEF2F2',
                    border: '1px solid #DC2626',
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: '#7F1D1D',
                    marginBottom: 10,
                  }}
                >
                  <strong>⚠ BITCO loss runs not attached.</strong> We can accept your submission, but we cannot
                  submit it to BITCO until 5-year loss runs are attached — BITCO won't treat it as complete without
                  them, and a competing agency that has them would be taken ahead of you. Add loss runs now, or
                  upload them later from your submitted account.
                </div>
              )}
              {docs.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>No documents attached — you can add them after submitting.</div>
              ) : (
                docs.map((d) => (
                  <div className="doc-row" key={d.id}>
                    <div className={`doc-check ${d.attached ? 'on' : 'off'}`}>{d.attached ? <CheckIcon width={12} height={12} /> : null}</div>
                    <span className="doc-name">{d.label}</span>
                    <span className="doc-file">{d.attached ? d.fileName : d.required ? 'To upload' : '—'}</span>
                  </div>
                ))
              )}
            </div>

            {turnstileConfigured && (
              <div className="review-block">
                <h4>Verification</h4>
                <Turnstile onToken={setTurnstileToken} />
              </div>
            )}

            <div className="review-block">
              <h4>Secure your submission</h4>
              <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 10px' }}>
                Set a 4-digit PIN. After you submit you'll get a code — your <b>code + PIN</b> let you reopen this and message us from any device.
              </p>
              <input className="finput" inputMode="numeric" maxLength={4} placeholder="4-digit PIN" value={pin}
                onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                style={{ maxWidth: 160, letterSpacing: 6, fontWeight: 800, fontSize: 18, textAlign: 'center' }} />
            </div>

            <div className="wiz-foot">
              <button className="btn btn-ghost" onClick={back}><BackIcon width={16} height={16} /> Back</button>
              <button className="btn btn-accent" onClick={() => void submit()} disabled={busy}>{busy ? 'Submitting…' : 'Submit to Agents Alliance'}</button>
            </div>
          </>
        )}
      </div>

      {submitted && (
        <Modal
          title={submitted.draft ? 'Progress saved' : 'Submission received'}
          subtitle="Write these down — your code and PIN are the only way back in."
          onClose={() => navigate('/', { state: { justSubmitted: { code: submitted.id, pin: submitted.pin } } })}
          footer={<button className="btn btn-primary" onClick={() => navigate('/', { state: { justSubmitted: { code: submitted.id, pin: submitted.pin } } })}>Done</button>}
        >
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 130, background: 'var(--slate-soft)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Your code</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy)', letterSpacing: 1 }}>{submitted.id}</div>
            </div>
            <div style={{ flex: 1, minWidth: 130, background: 'var(--slate-soft)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Your PIN</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy)', letterSpacing: 4 }}>{submitted.pin}</div>
            </div>
          </div>
        </Modal>
      )}

      {showSavePin && (
        <Modal
          title="Save your progress"
          subtitle="Set a 4-digit PIN. Your code + PIN let you pick up right where you left off."
          onClose={() => setShowSavePin(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setShowSavePin(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void doSaveDraft()} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            </>
          }
        >
          <input className="finput" inputMode="numeric" maxLength={4} placeholder="4-digit PIN" value={savePin}
            onChange={(e) => setSavePin(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
            style={{ maxWidth: 170, letterSpacing: 6, fontWeight: 800, fontSize: 18, textAlign: 'center' }} />
        </Modal>
      )}

      {showLibertyConfirm && (
        <Modal
          title="Liberty Mutual — please confirm"
          onClose={() => setShowLibertyConfirm(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setShowLibertyConfirm(false)}>Cancel</button>
              <button className="btn btn-liberty" onClick={confirmLiberty}>I understand — choose Liberty Mutual</button>
            </>
          }
        >
          <div style={{ background: '#EAF1FB', border: '1px solid #C4D6F1', borderLeft: '3px solid #1B4CA1', borderRadius: 10, padding: '13px 15px', fontSize: 13.5, color: '#1B3A6B', lineHeight: 1.55 }}>
            {LIBERTY_APPOINTMENT_NOTE}
          </div>
        </Modal>
      )}

      {showNoDocs && (
        <Modal
          title="Submit without documents?"
          onClose={() => setShowNoDocs(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => { setShowNoDocs(false); setStep(4); window.scrollTo({ top: 0 }); }}>
                <BackIcon width={16} height={16} /> Go back &amp; attach
              </button>
              <button className="btn btn-primary" onClick={() => void doSubmit()} disabled={busy}>
                {busy ? 'Submitting…' : 'Submit without documents'}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 13.5, color: 'var(--text)', margin: '0 0 12px', lineHeight: 1.55 }}>
            You haven’t attached any documents to this submission.
          </p>
          <div className="banner warn" style={{ margin: 0 }}>
            <div style={{ fontSize: 13, display: 'flex', gap: 8 }}>
              <InfoIcon width={16} height={16} />
              <span>
                {gk || liberty ? (
                  <>This is a required-carrier submission — <b>AAS can’t submit to the carrier until the package is complete</b> (ACORDs, supplementals, loss runs, dec pages). You can submit now and upload the rest anytime with your code + PIN.</>
                ) : (
                  <>If you’ve completed any forms — <b>ACORDs, a Discovery / subsheet, loss runs, or dec pages</b> — attach them on the Documents step now to speed up quoting and avoid back-and-forth. You can still submit now and add them later with your code + PIN.</>
                )}
              </span>
            </div>
          </div>
        </Modal>
      )}

      {showRushWarning && (
        <Modal
          title="A note on your timeline"
          onClose={() => setShowRushWarning(false)}
          footer={
            <button className="btn btn-primary" onClick={() => setShowRushWarning(false)}>
              Got it — continue
            </button>
          }
        >
          <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            We usually need about <b>7 business days</b> to quote an account well, and the date you picked is sooner
            than that. We can give it a shot, but <b>we can't promise we'll have it done by then</b>.
            <br />
            <br />
            If this account truly has to be in place by that date, you may want to line up an alternative in parallel
            so you're covered either way. Otherwise, go ahead and submit — we'll move as fast as we can.
          </p>
        </Modal>
      )}

      {showDocConfirm && (
        <Modal
          title="One thing to confirm"
          onClose={() => setShowDocConfirm(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setShowDocConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmDocsAndReview}>Continue in {TIERS[tierKey as TierKey].name}</button>
            </>
          }
        >
          <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '0 0 14px' }}>
            You're submitting under <b>{TIERS[tierKey as TierKey].name}</b> with a reason for one or more required documents instead of the document itself. <b>Your submission stays in {TIERS[tierKey as TierKey].name}</b> — this is just a heads-up.
          </p>
          <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: 13, color: '#92400E' }}>
            <b>If</b> it turns out we have to track these documents down for you, we may later move the submission to <b>Tier 1 · Full-Service Referral</b> and the commission split would change accordingly. If the documents simply don't exist yet — e.g. a brand-new venture with no loss runs — it stays as-is.
          </div>
          <div style={{ background: 'var(--slate-soft)', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>How the splits compare</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span>{TIERS[tierKey as TierKey].name} (current)</span>
              <span><b style={{ color: 'var(--orange)' }}>{TIERS[tierKey as TierKey].member}% you</b> · {TIERS[tierKey as TierKey].aas}% AAS</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span>{TIERS.complete.name}</span>
              <span><b>{TIERS.complete.member}% you</b> · {TIERS.complete.aas}% AAS</span>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// A carrier logo on a white "brand chip". Falls back to the carrier name if the image
// fails to load (or hides entirely when hideOnFail is set, e.g. the Liberty card whose
// title already names the carrier).
function CarrierLogo({ name, logo, big, hideOnFail, bare }: { name: string; logo?: string; big?: boolean; hideOnFail?: boolean; bare?: boolean }) {
  const [failed, setFailed] = useState(!logo);
  if (failed && hideOnFail) return null;
  // Bare = just the image (no white tile) for use on an already-light surface.
  if (bare) {
    return failed ? (
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)' }}>{name}</span>
    ) : (
      <img className="gk-logo-bare" src={logo} alt={name} onError={() => setFailed(true)} />
    );
  }
  return (
    <span className={`gk-logo-chip${big ? ' gk-logo-chip-lg' : ''}`} title={name}>
      {failed ? (
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)' }}>{name}</span>
      ) : (
        <img src={logo} alt={name} onError={() => setFailed(true)} />
      )}
    </span>
  );
}

// ---- field helpers ----
function Field({ label, field, value, onChange, err, opt, placeholder, format }: {
  label: string; field: string; value: string; onChange: (k: string, v: string) => void; err?: string; opt?: boolean; placeholder?: string; format?: (v: string) => string;
}) {
  return (
    <div className="fgroup">
      <label className="flabel">{label}{opt && <span className="opt"> (optional)</span>}</label>
      <input className={`finput${err ? ' err' : ''}`} value={value} placeholder={placeholder} onChange={(e) => onChange(field, format ? format(e.target.value) : e.target.value)} />
      {err && <div className="ferr show">{err}</div>}
    </div>
  );
}
function Area({ label, field, value, onChange, err, opt, placeholder }: {
  label: string; field: string; value: string; onChange: (k: string, v: string) => void; err?: string; opt?: boolean; placeholder?: string;
}) {
  return (
    <div className="fgroup">
      <label className="flabel">{label}{opt && <span className="opt"> (optional)</span>}</label>
      <textarea className={`finput${err ? ' err' : ''}`} rows={3} value={value} placeholder={placeholder} onChange={(e) => onChange(field, e.target.value)} />
      {err && <div className="ferr show">{err}</div>}
    </div>
  );
}
